import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Plus, Trash2, Users, DollarSign, Clock, Lightbulb, Scale, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';

interface Founder {
  id: string;
  name: string;
  role: string;
  timeCommitment: number; // percentage (0-100)
  ipValue: number; // 0-10 scale
  cashInvestment: number; // dollars
  experience: number; // years
  vestingMonths: number;
  cliffMonths: number;
}

interface FactorWeights {
  role: number;
  time: number;
  ip: number;
  cash: number;
  experience: number;
}

interface EquitySplit {
  founderId: string;
  founderName: string;
  roleScore: number;
  timeScore: number;
  ipScore: number;
  cashScore: number;
  experienceScore: number;
  totalScore: number;
  equityPercentage: number;
  shares: number;
}

const roleScores: Record<string, number> = {
  CEO: 10,
  CTO: 9,
  COO: 8,
  CFO: 8,
  CMO: 7,
  'VP Engineering': 7,
  'VP Product': 7,
  'VP Sales': 7,
  'Head of': 6,
  Other: 5,
};

export default function FounderEquitySplitCalculator() {
  const [founders, setFounders] = useState<Founder[]>([
    {
      id: '1',
      name: 'Founder 1',
      role: 'CEO',
      timeCommitment: 100,
      ipValue: 8,
      cashInvestment: 50000,
      experience: 10,
      vestingMonths: 48,
      cliffMonths: 12,
    },
    {
      id: '2',
      name: 'Founder 2',
      role: 'CTO',
      timeCommitment: 100,
      ipValue: 9,
      cashInvestment: 25000,
      experience: 8,
      vestingMonths: 48,
      cliffMonths: 12,
    },
  ]);

  const [newFounder, setNewFounder] = useState<Founder>({
    id: '',
    name: '',
    role: 'Other',
    timeCommitment: 100,
    ipValue: 5,
    cashInvestment: 0,
    experience: 5,
    vestingMonths: 48,
    cliffMonths: 12,
  });

  const [weights, setWeights] = useState<FactorWeights>({
    role: 25,
    time: 25,
    ip: 20,
    cash: 15,
    experience: 15,
  });

  const [totalShares, setTotalShares] = useState(10000000);

  const addFounder = () => {
    if (newFounder.name) {
      setFounders([
        ...founders,
        {
          ...newFounder,
          id: Date.now().toString(),
        },
      ]);
      setNewFounder({
        id: '',
        name: '',
        role: 'Other',
        timeCommitment: 100,
        ipValue: 5,
        cashInvestment: 0,
        experience: 5,
        vestingMonths: 48,
        cliffMonths: 12,
      });
    }
  };

  const removeFounder = (id: string) => {
    setFounders(founders.filter((f) => f.id !== id));
  };

  const updateFounder = (id: string, updates: Partial<Founder>) => {
    setFounders(founders.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const normalizeScore = (value: number, max: number): number => {
    return max > 0 ? (value / max) * 10 : 0;
  };

  const calculateEquitySplit = (): EquitySplit[] => {
    if (founders.length === 0) return [];

    // Calculate max values for normalization
    const maxTime = Math.max(...founders.map((f) => f.timeCommitment));
    const maxIp = Math.max(...founders.map((f) => f.ipValue));
    const maxCash = Math.max(...founders.map((f) => f.cashInvestment));
    const maxExperience = Math.max(...founders.map((f) => f.experience));

    // Calculate scores for each founder
    const splits = founders.map((founder) => {
      const roleScore = roleScores[founder.role] || 5;
      const timeScore = normalizeScore(founder.timeCommitment, maxTime);
      const ipScore = normalizeScore(founder.ipValue, maxIp);
      const cashScore = normalizeScore(founder.cashInvestment, maxCash);
      const experienceScore = normalizeScore(founder.experience, maxExperience);

      // Calculate weighted total score
      const totalScore =
        (roleScore * weights.role) / 100 +
        (timeScore * weights.time) / 100 +
        (ipScore * weights.ip) / 100 +
        (cashScore * weights.cash) / 100 +
        (experienceScore * weights.experience) / 100;

      return {
        founderId: founder.id,
        founderName: founder.name,
        roleScore,
        timeScore,
        ipScore,
        cashScore,
        experienceScore,
        totalScore,
        equityPercentage: 0,
        shares: 0,
      };
    });

    // Normalize to percentages
    const totalScoreSum = splits.reduce((sum, s) => sum + s.totalScore, 0);
    splits.forEach((split) => {
      split.equityPercentage = totalScoreSum > 0 ? (split.totalScore / totalScoreSum) * 100 : 0;
      split.shares = Math.floor((split.equityPercentage / 100) * totalShares);
    });

    return splits;
  };

  const equitySplits = calculateEquitySplit();

  // Prepare chart data
  const pieData = equitySplits.map((split, idx) => ({
    name: split.founderName,
    value: split.equityPercentage,
    shares: split.shares,
  }));

  const factorBreakdownData = equitySplits.map((split) => ({
    name: split.founderName,
    Role: ((split.roleScore * weights.role) / 100).toFixed(1),
    Time: ((split.timeScore * weights.time) / 100).toFixed(1),
    IP: ((split.ipScore * weights.ip) / 100).toFixed(1),
    Cash: ((split.cashScore * weights.cash) / 100).toFixed(1),
    Experience: ((split.experienceScore * weights.experience) / 100).toFixed(1),
  }));

  const colors = [
    'hsl(var(--primary))',
    'hsl(var(--secondary))',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7c7c',
  ];

  const totalWeights = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const weightsValid = totalWeights === 100;

  // Calculate fairness metrics
  const maxEquity = Math.max(...equitySplits.map((s) => s.equityPercentage));
  const minEquity = Math.min(...equitySplits.map((s) => s.equityPercentage));
  const equityGap = maxEquity - minEquity;
  const avgEquity =
    equitySplits.reduce((sum, s) => sum + s.equityPercentage, 0) / equitySplits.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Founder Equity Split Calculator
          </CardTitle>
          <CardDescription>
            Calculate fair equity splits based on role, time, IP, cash, and experience contributions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Total Shares */}
          <div>
            <Label>Total Founder Shares</Label>
            <Input
              type="number"
              value={totalShares || ''}
              onChange={(e) => setTotalShares(Number(e.target.value))}
              placeholder="10000000"
            />
          </div>

          <Separator />

          {/* Factor Weights */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Factor Weights</h3>
              <Badge variant={weightsValid ? 'default' : 'destructive'}>
                Total: {totalWeights}% {!weightsValid && '(Must equal 100%)'}
              </Badge>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Role Importance
                  </Label>
                  <span className="text-sm font-medium">{weights.role}%</span>
                </div>
                <Slider
                  value={[weights.role]}
                  onValueChange={(value) => setWeights({ ...weights, role: value[0] })}
                  max={100}
                  step={5}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Time Commitment
                  </Label>
                  <span className="text-sm font-medium">{weights.time}%</span>
                </div>
                <Slider
                  value={[weights.time]}
                  onValueChange={(value) => setWeights({ ...weights, time: value[0] })}
                  max={100}
                  step={5}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    IP Contribution
                  </Label>
                  <span className="text-sm font-medium">{weights.ip}%</span>
                </div>
                <Slider
                  value={[weights.ip]}
                  onValueChange={(value) => setWeights({ ...weights, ip: value[0] })}
                  max={100}
                  step={5}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Cash Investment
                  </Label>
                  <span className="text-sm font-medium">{weights.cash}%</span>
                </div>
                <Slider
                  value={[weights.cash]}
                  onValueChange={(value) => setWeights({ ...weights, cash: value[0] })}
                  max={100}
                  step={5}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Experience
                  </Label>
                  <span className="text-sm font-medium">{weights.experience}%</span>
                </div>
                <Slider
                  value={[weights.experience]}
                  onValueChange={(value) => setWeights({ ...weights, experience: value[0] })}
                  max={100}
                  step={5}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Add New Founder */}
          <div className="space-y-4">
            <h3 className="font-semibold">Add Founder</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newFounder.name}
                  onChange={(e) => setNewFounder({ ...newFounder, name: e.target.value })}
                  placeholder="Founder name"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={newFounder.role}
                  onValueChange={(value) => setNewFounder({ ...newFounder, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CEO">CEO</SelectItem>
                    <SelectItem value="CTO">CTO</SelectItem>
                    <SelectItem value="COO">COO</SelectItem>
                    <SelectItem value="CFO">CFO</SelectItem>
                    <SelectItem value="CMO">CMO</SelectItem>
                    <SelectItem value="VP Engineering">VP Engineering</SelectItem>
                    <SelectItem value="VP Product">VP Product</SelectItem>
                    <SelectItem value="VP Sales">VP Sales</SelectItem>
                    <SelectItem value="Head of">Head of</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time Commitment (%)</Label>
                <Input
                  type="number"
                  value={newFounder.timeCommitment || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, timeCommitment: Number(e.target.value) })
                  }
                  placeholder="100"
                  max={100}
                  min={0}
                />
              </div>
              <div>
                <Label>IP Value (0-10)</Label>
                <Input
                  type="number"
                  value={newFounder.ipValue || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, ipValue: Number(e.target.value) })
                  }
                  placeholder="5"
                  max={10}
                  min={0}
                />
              </div>
              <div>
                <Label>Cash Investment ($)</Label>
                <Input
                  type="number"
                  value={newFounder.cashInvestment || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, cashInvestment: Number(e.target.value) })
                  }
                  placeholder="50000"
                />
              </div>
              <div>
                <Label>Experience (Years)</Label>
                <Input
                  type="number"
                  value={newFounder.experience || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, experience: Number(e.target.value) })
                  }
                  placeholder="10"
                />
              </div>
              <div>
                <Label>Vesting (Months)</Label>
                <Input
                  type="number"
                  value={newFounder.vestingMonths || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, vestingMonths: Number(e.target.value) })
                  }
                  placeholder="48"
                />
              </div>
              <div>
                <Label>Cliff (Months)</Label>
                <Input
                  type="number"
                  value={newFounder.cliffMonths || ''}
                  onChange={(e) =>
                    setNewFounder({ ...newFounder, cliffMonths: Number(e.target.value) })
                  }
                  placeholder="12"
                />
              </div>
            </div>
            <Button onClick={addFounder} disabled={!newFounder.name} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Founder
            </Button>
          </div>

          <Separator />

          {/* Current Founders */}
          <div className="space-y-2">
            <h3 className="font-semibold">Current Founders</h3>
            {founders.map((founder) => (
              <div key={founder.id} className="p-3 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium">{founder.name}</div>
                      <div className="text-sm text-muted-foreground">{founder.role}</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeFounder(founder.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Time:</span> {founder.timeCommitment}%
                  </div>
                  <div>
                    <span className="text-muted-foreground">IP:</span> {founder.ipValue}/10
                  </div>
                  <div>
                    <span className="text-muted-foreground">Cash:</span> $
                    {founder.cashInvestment.toLocaleString()}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Experience:</span> {founder.experience}{' '}
                    years
                  </div>
                  <div>
                    <span className="text-muted-foreground">Vesting:</span> {founder.vestingMonths}
                    mo
                  </div>
                  <div>
                    <span className="text-muted-foreground">Cliff:</span> {founder.cliffMonths}mo
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {founders.length > 0 && weightsValid && (
        <>
          {/* Summary Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Equity Split Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Founders</div>
                  <div className="text-2xl font-bold">{founders.length}</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Largest Share</div>
                  <div className="text-2xl font-bold">{maxEquity.toFixed(1)}%</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Smallest Share</div>
                  <div className="text-2xl font-bold">{minEquity.toFixed(1)}%</div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Equity Gap</div>
                  <div className="text-2xl font-bold">{equityGap.toFixed(1)}%</div>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Founder</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Total Score</TableHead>
                    <TableHead className="text-right">Equity %</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                    <TableHead>Vesting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equitySplits.map((split) => {
                    const founder = founders.find((f) => f.id === split.founderId);
                    return (
                      <TableRow key={split.founderId}>
                        <TableCell className="font-medium">{split.founderName}</TableCell>
                        <TableCell>{founder?.role}</TableCell>
                        <TableCell className="text-right">{split.totalScore.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-bold">
                          {split.equityPercentage.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">
                          {split.shares.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {founder?.vestingMonths}mo / {founder?.cliffMonths}mo cliff
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Equity Distribution Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Equity Distribution</CardTitle>
              <CardDescription>Visual breakdown of founder equity allocation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {pieData.map((data, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: colors[idx % colors.length] }}
                        />
                        <span className="font-medium">{data.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{data.value.toFixed(2)}%</div>
                        <div className="text-sm text-muted-foreground">
                          {data.shares.toLocaleString()} shares
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Factor Contribution Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Contribution Factor Breakdown</CardTitle>
              <CardDescription>How each factor contributes to equity allocation</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={factorBreakdownData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Weighted Score', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Role" stackId="a" fill="#8884d8" />
                  <Bar dataKey="Time" stackId="a" fill="#82ca9d" />
                  <Bar dataKey="IP" stackId="a" fill="#ffc658" />
                  <Bar dataKey="Cash" stackId="a" fill="#ff7c7c" />
                  <Bar dataKey="Experience" stackId="a" fill="#a78bfa" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Fairness Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Fairness Analysis & Recommendations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {equityGap > 30 && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium">⚠️ Large Equity Gap</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {equityGap.toFixed(1)}% gap between largest and smallest equity holders.
                    Consider if this reflects true contribution differences or if it might create
                    tension.
                  </p>
                </div>
              )}

              {equityGap < 10 && founders.length > 1 && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-sm font-medium">✅ Balanced Split</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Small equity gap ({equityGap.toFixed(1)}%) suggests balanced contributions.
                    Equal or near-equal splits can promote team cohesion.
                  </p>
                </div>
              )}

              {founders.some((f) => f.timeCommitment < 100) && (
                <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm font-medium">💡 Part-Time Founders</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some founders are part-time. Consider longer vesting periods or performance
                    milestones for part-time contributors to align incentives.
                  </p>
                </div>
              )}

              {founders.some((f) => f.vestingMonths !== 48 || f.cliffMonths !== 12) && (
                <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-lg">
                  <p className="text-sm font-medium">📊 Non-Standard Vesting</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Some founders have non-standard vesting. Industry standard is 4-year vesting
                    with 1-year cliff for all founders to align long-term commitment.
                  </p>
                </div>
              )}

              <div className="p-3 bg-muted/50 border rounded-lg">
                <p className="text-sm font-medium">Best Practice Guidelines</p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  <li>Use 4-year vesting with 1-year cliff for all founders</li>
                  <li>Document equity agreements in writing before incorporation</li>
                  <li>Consider reverse vesting to protect against early departures</li>
                  <li>Revisit splits if roles or contributions significantly change</li>
                  <li>Reserve 10-20% for future employee option pool</li>
                  <li>Get legal review of founder agreements and vesting terms</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!weightsValid && (
        <Card className="border-destructive">
          <CardContent className="py-8">
            <div className="text-center space-y-2">
              <p className="text-muted-foreground">
                Factor weights must total 100% to calculate equity splits.
              </p>
              <p className="text-sm text-muted-foreground">Current total: {totalWeights}%</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
